/**
 * Public entry for the Tashimoto Support widget.
 *
 * Surface:
 *   new TashimotoSupport({ publicKey, zIndex? }).launch() / .destroy()
 *
 * The IIFE build assigns the class to `globalThis.TashimotoSupport`.
 *
 * Isolation model (see plans/SUPPORT_WIDGET_ISOLATION_PLAN.md):
 *   - The widget renders inside an open Shadow Root attached to a fixed,
 *     full-viewport host div. Host CSS cannot cascade into the widget and
 *     widget CSS (Tailwind Preflight + component styles) cannot escape.
 *   - Host is `pointer-events: none`; the mount div inside the shadow root
 *     is `pointer-events: auto` so only the rendered widget (bubble/frame)
 *     captures clicks — empty space passes events through to the host page.
 *   - All CSS is inlined into this JS bundle at build time (see vite.config
 *     `inlineWidgetCss` plugin) and injected as a single <style> at the top
 *     of the shadow root. No <link> or <style> touches document.head.
 */

import { mount, unmount } from 'svelte';
import App from './App.svelte';
import { appState, resetState } from './lib/state.svelte';
import type { ConstructorOptions } from './lib/types';

const TARGET_ID = 'tashimoto-support-widget';
const AUTOBOOT_REF = '__tashimotoSupport';
// Int32 max is 2147483647; leaving headroom lets host pages stack above us
// if they really need to, without us saturating the browser's layer index.
const MAX_Z_INDEX = 2147483000;

// The literal string below is replaced at build time with the full
// compiled CSS bundle (Tailwind + every Svelte component style) by the
// `inlineWidgetCss` plugin in vite.config.js. The replacement happens
// at the string-literal site inside the prod branch of `launch()` —
// keeping the literal there (and never comparing it to a sibling const)
// prevents Rollup from dead-code-eliminating the branch before the
// plugin's generateBundle hook can swap it.

export class TashimotoSupport {
  private readonly publicKey: string;
  private readonly zIndex: number;
  private readonly baseUrl: string | undefined;
  private app: ReturnType<typeof mount> | null = null;
  private hostEl: HTMLElement | null = null;
  private devStyleObserver: MutationObserver | null = null;
  private devStyleMirrors: Map<Element, Element> = new Map();

  constructor(opts: ConstructorOptions) {
    if (!opts || typeof opts !== 'object') {
      throw new Error('TashimotoSupport: options object is required');
    }
    if (!opts.publicKey || typeof opts.publicKey !== 'string') {
      throw new Error('TashimotoSupport: `publicKey` is required');
    }
    this.publicKey = opts.publicKey;
    const z =
      typeof opts.zIndex === 'number' && isFinite(opts.zIndex) ? opts.zIndex : 100;
    // Clamp to a sane max; negative z-index is permitted (visitor may want
    // the widget behind specific overlays, e.g. a modal the host manages).
    this.zIndex = z > MAX_Z_INDEX ? MAX_Z_INDEX : z;
    this.baseUrl = opts.baseUrl;
  }

  async launch(): Promise<void> {
    if (this.app) return;
    if (typeof document === 'undefined') {
      throw new Error('TashimotoSupport: DOM is not available');
    }

    resetState();
    appState.publicKey = this.publicKey;
    appState.zIndex = this.zIndex;

    // ── Host element: fixed, full-viewport, inert ─────────────────────
    // `pointer-events: none` so empty areas of the widget let host-page
    // clicks through. The mount div inside the shadow root re-enables
    // pointer events for the rendered widget surface.
    const host = document.createElement('div');
    host.id = TARGET_ID;
    host.style.position = 'fixed';
    host.style.top = '0';
    host.style.left = '0';
    host.style.width = '0';
    host.style.height = '0';
    host.style.pointerEvents = 'none';
    host.style.zIndex = String(this.zIndex);
    document.body.appendChild(host);
    this.hostEl = host;

    // ── Shadow Root ───────────────────────────────────────────────────
    // `mode: 'open'` lets devtools inspect + tests reach in. Closed mode
    // would hide implementation details but devs debugging an embed on a
    // customer site should be able to see what's rendering.
    const shadow = host.attachShadow({ mode: 'open' });

    if (import.meta.env.DEV) {
      // Dev: Vite HMR injects component CSS as <style> tags in
      // document.head, which can't cross the shadow boundary. Mirror those
      // into the shadow root and keep the mirror in sync with HMR updates
      // so `npm run dev` renders the same as a production build.
      this.startDevStyleMirror(shadow);
    } else {
      // Prod: the literal below is swapped for the concatenated Tailwind +
      // component stylesheet by the `inlineWidgetCss` plugin. Must remain
      // a bare string literal (not assigned to a const compared against
      // itself), otherwise Rollup constant-folds the comparison and drops
      // this whole branch before the plugin runs.
      const styleEl = document.createElement('style');
      styleEl.textContent = '__TASHIMOTO_WIDGET_CSS__';
      shadow.appendChild(styleEl);
    }

    // Mount target lives inside the shadow root. `pointer-events: auto`
    // inherits down into the widget (Bubble/Frame/WelcomePopup are all
    // position: fixed, so they render outside the mount div's layout box
    // but still inherit pointer-events from it via CSS inheritance).
    const mountTarget = document.createElement('div');
    mountTarget.style.pointerEvents = 'auto';
    shadow.appendChild(mountTarget);

    this.app = mount(App as any, {
      target: mountTarget,
      props: this.baseUrl ? { baseUrl: this.baseUrl } : {},
    });
  }

  private startDevStyleMirror(shadow: ShadowRoot): void {
    const isStyleLike = (el: Element): boolean =>
      el.tagName === 'STYLE' ||
      (el.tagName === 'LINK' &&
        (el as HTMLLinkElement).rel === 'stylesheet');

    const sync = (): void => {
      // Drop mirrors whose source <style> / <link> was removed from head.
      for (const [src, mirror] of Array.from(this.devStyleMirrors)) {
        if (!document.head.contains(src)) {
          try { mirror.remove(); } catch { /* noop */ }
          this.devStyleMirrors.delete(src);
        }
      }
      // Clone any newly-inserted head stylesheets into the shadow root.
      for (const el of Array.from(document.head.children)) {
        if (!isStyleLike(el)) continue;
        if (this.devStyleMirrors.has(el)) continue;
        const clone = el.cloneNode(true) as Element;
        shadow.appendChild(clone);
        this.devStyleMirrors.set(el, clone);
      }
      // Vite HMR mutates a <style>'s textContent in place rather than
      // replacing the element — keep mirrors in sync on every tick.
      for (const [src, mirror] of this.devStyleMirrors) {
        if (src.tagName !== 'STYLE') continue;
        if (mirror.textContent !== src.textContent) {
          mirror.textContent = src.textContent;
        }
      }
    };

    sync();
    this.devStyleObserver = new MutationObserver(sync);
    this.devStyleObserver.observe(document.head, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  destroy(): void {
    // Idempotent: safe to call multiple times; second call is a no-op.
    if (this.devStyleObserver) {
      try {
        this.devStyleObserver.disconnect();
      } catch {
        // noop
      }
      this.devStyleObserver = null;
    }
    this.devStyleMirrors.clear();
    if (this.app) {
      try {
        unmount(this.app);
      } catch {
        // noop — best-effort teardown
      }
      this.app = null;
    }
    if (this.hostEl) {
      try {
        this.hostEl.remove();
      } catch {
        // noop
      }
      this.hostEl = null;
    }
    // Clear the auto-boot reference so a subsequent embed can succeed.
    if (
      typeof globalThis !== 'undefined' &&
      (globalThis as any)[AUTOBOOT_REF] === this
    ) {
      try {
        delete (globalThis as any)[AUTOBOOT_REF];
      } catch {
        (globalThis as any)[AUTOBOOT_REF] = undefined;
      }
    }
    resetState();
  }
}

// IIFE global so the embed snippet works without module support.
if (typeof globalThis !== 'undefined') {
  (globalThis as any).TashimotoSupport = TashimotoSupport;
}

// ─── Auto-boot ───────────────────────────────────────────────────────────
// If the host page has defined a TASHIMOTO_SUPPORT_KEY global, the widget
// launches itself on DOM ready — no explicit `new TashimotoSupport(...)`
// call required in the embed markup.
//
// Supported ways to set the key:
//   <script>window.TASHIMOTO_SUPPORT_KEY = 'pk_xxx';</script>
//   <script>var TASHIMOTO_SUPPORT_KEY = 'pk_xxx';</script>  // top-level var → on window
//   <script>const TASHIMOTO_SUPPORT_KEY = 'pk_xxx';</script>
// The last form creates a script-scoped lexical binding that isn't on window
// but is still reachable via indirect eval from our IIFE scope. The eval
// is wrapped in try/catch so a strict CSP that forbids eval simply falls
// back to the window-scoped branch above.
function readGlobalConfig(name: string): any {
  const g: any = globalThis;
  if (g && typeof g[name] !== 'undefined') return g[name];
  try {
    // Indirect eval — runs in global scope, can see top-level const/let.
    return (0, eval)('typeof ' + name + ' !== "undefined" ? ' + name + ' : undefined');
  } catch {
    return undefined;
  }
}

let autoBootInstance: TashimotoSupport | null = null;
function autoBoot(): void {
  if (autoBootInstance) return;
  const key = readGlobalConfig('TASHIMOTO_SUPPORT_KEY');
  if (typeof key !== 'string' || !key) return;
  const zIndexRaw = readGlobalConfig('TASHIMOTO_SUPPORT_ZINDEX');
  const zIndex = typeof zIndexRaw === 'number' && isFinite(zIndexRaw) ? zIndexRaw : undefined;
  // Per-embed override; otherwise App.svelte uses the build-time default
  // (dev → http://localhost:5010, prod → https://app.tashimoto.com).
  const baseUrlRaw = readGlobalConfig('TASHIMOTO_SUPPORT_BASE_URL');
  const baseUrl = typeof baseUrlRaw === 'string' && baseUrlRaw ? baseUrlRaw : undefined;
  try {
    autoBootInstance = new TashimotoSupport({ publicKey: key, zIndex, baseUrl });
    (globalThis as any)[AUTOBOOT_REF] = autoBootInstance;
    void autoBootInstance.launch();
  } catch (err) {
    console.error('TashimotoSupport auto-boot failed:', err);
    autoBootInstance = null;
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    // Self-removing listener so we don't leak on the host document after
    // boot completes (or after the script is re-embedded).
    const onReady = () => {
      document.removeEventListener('DOMContentLoaded', onReady);
      autoBoot();
    };
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    // Defer by a microtask so any trailing inline <script> that happens
    // to run after us still gets a chance to set TASHIMOTO_SUPPORT_KEY.
    // queueMicrotask is synchronous with the next microtask checkpoint —
    // doesn't keep a timer pending if the page navigates away.
    queueMicrotask(autoBoot);
  }
}

export default TashimotoSupport;
